import type { FC } from 'react';

import File from '@crehana/compass.icons.outline/File';
import { useTranslation } from '@crehana/i18n';
// biome-ignore lint/style/noRestrictedImports: legacy import, will be migrated
import { Container, ProgressBar } from '@crehana/ui';

import CardContainer from '@/views/AdminDashboard/components/CardContainer';
import { useExcelImporterStepperContext } from '@/views/AdminDashboard/components/ExcelImporterStepper/context';
import Validations from '@/views/AdminDashboard/components/Validations';
import useAmplitudeLog from '@/views/AdminDashboard/hooks/useAmplitudeLog';

import { useDataValidation } from './hooks';
import {
  ValidationConfigErrorHandlerEnum,
  ValidationConfigPopperHandlerEnum,
} from './types';

const AMPLITUDE_SOURCE = 'Massive Upload Users Step Progress - TalentSuit';

const Step2: FC<React.PropsWithChildren> = () => {
  const { t } = useTranslation();
  const { amplitudeEvent } = useAmplitudeLog();
  const {
    validations,
    templateUrl,
    handleNextStep,
    handlePreviousStep,
    validationStepVisited,
    shouldShow,
    isDark,
  } = useExcelImporterStepperContext();

  const {
    validations: validationsState,
    percent,
    isSuccessBar,
  } = useDataValidation(validations.map(({ codes }) => codes));

  const handleDownloadTemplate = () => {
    window.location.href = templateUrl;
  };

  //TODO: Check why this is not working
  const getPopperHandler = (
    popperHandler?: ValidationConfigPopperHandlerEnum,
  ) => {
    switch (popperHandler) {
      case ValidationConfigPopperHandlerEnum.DOWNLOAD_TEMPLATE:
        return handleDownloadTemplate;
      case ValidationConfigPopperHandlerEnum.PREVIOUS_STEP:
        return handlePreviousStepProgress;
      default:
        return undefined;
    }
  };

  const handlePreviousStepProgress = () => {
    amplitudeEvent({
      event: 'Error - Try again Step 1',
      data: {
        Source: AMPLITUDE_SOURCE,
      },
    });
    handlePreviousStep();
  };

  const validationConfigWithEvents = validations.map(validation => {
    const { handler, ...popperWithoutHandler } = validation?.popper || {};
    return {
      ...validation,
      popper: {
        ...popperWithoutHandler,
        onClick: getPopperHandler(handler),
      },
      handleError:
        validation.handleError === ValidationConfigErrorHandlerEnum.SHOW_DETAIL
          ? () => handleNextStep(validation.codes)
          : undefined,
    };
  });

  const textStatusColor = isSuccessBar
    ? `text-${isDark ? 'white' : 'base-dark'}`
    : `text-${isDark ? 'red-light' : 'red-main'}`;

  const statusTitle = `${percent}%${
    isSuccessBar ? '' : ` - ${t('STEP_2_ERROR_TITLE')}`
  }`;

  return (
    <div className="flex flex-col items-center pt-56 pb-20 w-full">
      {shouldShow(['step2', 'all']) && (
        <div className="pt-56 tw-pb-4">
          <p className="tw-font-h4 tw-text-center dark:tw-text-neutral-light-100 tw-mb-0.4">
            {t('STEP_2_TITLE')}
          </p>
          <p className="tw-font-body4 tw-text-neutral-light-700 tw-text-center">
            {t('STEP_2_SUBTITLE')}
          </p>
        </div>
      )}
      <Container>
        <CardContainer
          isDark={isDark}
          title={t('STEP_2_TABLE_TITLE')}
          icon={<File size={24} className="tw-text-neutral-light-600" />}
        >
          <p
            className={`tw-font-subtitle5 tw-text-center tw-pb-1.2 ${textStatusColor}`}
          >
            {statusTitle}
          </p>
          <ProgressBar
            full
            size="s"
            progress={!validationStepVisited ? percent : 100}
            transitionTime={!validationStepVisited ? 1500 : 0}
            barClassName={isSuccessBar ? 'bg-primary-main' : 'bg-red-main'}
          />
          <p className="tw-font-subtitle5 tw-text-left pb-16 pt-36">
            {t('STEP_2_VALIDATION_TITLE')}
          </p>
          <Validations
            isDark={isDark}
            validations={validationConfigWithEvents.map(
              (validation, index) => ({
                ...validation,
                state: validationsState[index],
              }),
            )}
          />
        </CardContainer>
      </Container>
    </div>
  );
};

export default Step2;
